/**
 * Formulário do endereço de faturamento (tomador da NFS-e) — controle de
 * faturamento, out/2026. Usado pelo cliente em /conta, pelo admin em
 * /admin/faturamento e pelo pedido de endereço do 1º serviço adicional
 * (`BillingAddressPrompt`). O CEP consulta o ViaCEP direto do navegador e
 * preenche cidade/UF (e o logradouro, se vazio); o número e o complemento
 * ficam por conta de quem preenche. O backend normaliza e valida.
 *
 * `BillingAddressFields` (out/2026) são só os campos, controlados por quem
 * usa — o cadastro com plano pede o endereço no meio do próprio formulário,
 * sem o botão "Salvar endereço". Cores pelos tokens do tema (out/2026; eram
 * hexadecimais fixos).
 */
import React, { useEffect, useState } from 'react';
import styled from 'styled-components';

import { BillingInfo } from 'services/billing.service';

export interface BillingAddressValue {
  billingAddress: string;
  billingZipCode: string;
  billingCity: string;
  billingState: string;
}

export const emptyBillingAddress: BillingAddressValue = {
  billingAddress: '',
  billingZipCode: '',
  billingCity: '',
  billingState: '',
};

const onlyDigits = (v: string) => v.replace(/\D/g, '');

/** Endereço completo (os quatro campos, CEP com 8 dígitos). */
export const isBillingAddressComplete = (v: BillingAddressValue): boolean =>
  !!v.billingAddress.trim() &&
  onlyDigits(v.billingZipCode).length === 8 &&
  !!v.billingCity.trim() &&
  v.billingState.trim().length === 2;

interface FieldsProps {
  value: BillingAddressValue;
  onChange: (value: BillingAddressValue) => void;
  // Mensagens do backend por campo (billingAddress, billingZipCode…)
  errors?: Partial<Record<keyof BillingAddressValue, string>>;
}

export const BillingAddressFields = ({
  value,
  onChange,
  errors,
}: FieldsProps) => {
  const [zipError, setZipError] = useState('');

  const lookupZip = async (zip: string) => {
    const digits = onlyDigits(zip);
    if (digits.length !== 8) return;
    try {
      const res = await fetch(`https://viacep.com.br/ws/${digits}/json/`);
      const data = await res.json();
      if (data?.erro) {
        setZipError('CEP não encontrado.');
        return;
      }
      setZipError('');
      onChange({
        ...value,
        billingCity: data.localidade || value.billingCity,
        billingState: data.uf || value.billingState,
        billingAddress:
          value.billingAddress ||
          [data.logradouro, data.bairro].filter(Boolean).join(', '),
      });
    } catch {
      // ViaCEP fora do ar: segue o preenchimento manual
    }
  };

  const fieldError = zipError || Object.values(errors ?? {}).find(Boolean);

  return (
    <Fields>
      <label className="zip">
        CEP
        <input
          value={value.billingZipCode}
          maxLength={9}
          placeholder="00000-000"
          onChange={(e) =>
            onChange({ ...value, billingZipCode: e.target.value })
          }
          onBlur={(e) => lookupZip(e.target.value)}
        />
      </label>
      <label className="address">
        Endereço (rua, número, complemento, bairro)
        <input
          value={value.billingAddress}
          maxLength={500}
          onChange={(e) =>
            onChange({ ...value, billingAddress: e.target.value })
          }
        />
      </label>
      <label className="city">
        Cidade
        <input
          value={value.billingCity}
          maxLength={120}
          onChange={(e) => onChange({ ...value, billingCity: e.target.value })}
        />
      </label>
      <label className="state">
        UF
        <input
          value={value.billingState}
          maxLength={2}
          onChange={(e) =>
            onChange({ ...value, billingState: e.target.value.toUpperCase() })
          }
        />
      </label>
      {fieldError && <ErrorText>{fieldError}</ErrorText>}
    </Fields>
  );
};

interface Props {
  initial: BillingInfo;
  onSave: (info: BillingInfo) => Promise<void>;
  saving?: boolean;
  // Endereço completo obrigatório (pedido do 1º serviço adicional)
  requireComplete?: boolean;
}

export const BillingInfoForm = ({
  initial,
  onSave,
  saving,
  requireComplete,
}: Props) => {
  const [form, setForm] = useState<BillingAddressValue>(emptyBillingAddress);
  const [error, setError] = useState('');

  useEffect(() => {
    setForm({
      billingAddress: initial.billingAddress ?? '',
      billingZipCode: initial.billingZipCode ?? '',
      billingCity: initial.billingCity ?? '',
      billingState: initial.billingState ?? '',
    });
  }, [initial]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const zip = onlyDigits(form.billingZipCode);
    if (zip && zip.length !== 8) {
      setError('O CEP deve ter 8 dígitos.');
      return;
    }
    if (requireComplete && !isBillingAddressComplete(form)) {
      setError('Preencha CEP, endereço, cidade e UF.');
      return;
    }
    setError('');
    await onSave({
      billingAddress: form.billingAddress.trim() || null,
      billingZipCode: zip || null,
      billingCity: form.billingCity.trim() || null,
      billingState: form.billingState.trim().toUpperCase() || null,
    });
  };

  return (
    <Form onSubmit={handleSubmit}>
      <BillingAddressFields
        value={form}
        onChange={setForm}
        errors={error ? { billingZipCode: error } : undefined}
      />
      <button type="submit" disabled={saving}>
        {saving ? 'Salvando…' : 'Salvar endereço'}
      </button>
    </Form>
  );
};

const Fields = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px 12px;
  align-items: flex-end;
  flex: 1 1 100%;

  label {
    display: flex;
    flex-direction: column;
    gap: 4px;
    font-size: 12px;
    color: ${(props) => props.theme.color.text.quaternary};
  }

  label.zip {
    width: 110px;
  }
  label.address {
    flex: 1 1 280px;
  }
  label.city {
    flex: 0 1 180px;
  }
  label.state {
    width: 56px;
  }

  input {
    padding: 6px 0;
    border: none;
    border-bottom: 1px solid ${(props) => props.theme.color.ui.shade2};
    background: transparent;
    font-size: 14px;
    color: ${(props) => props.theme.color.text.primary};
    width: 100%;

    &:focus {
      outline: none;
      border-bottom-color: ${(props) => props.theme.color.ui.primary};
    }
  }
`;

const Form = styled.form`
  display: flex;
  flex-wrap: wrap;
  gap: 8px 12px;
  align-items: flex-end;

  button {
    padding: 7px 14px;
    border-radius: 3px;
    border: none;
    background-color: ${(props) => props.theme.color.ui.primary};
    color: ${(props) => props.theme.color.text.inverse};
    font-size: 13px;
    font-weight: 700;
    cursor: pointer;

    &:disabled {
      opacity: 0.6;
      cursor: default;
    }
  }
`;

const ErrorText = styled.span`
  flex-basis: 100%;
  font-size: 12px;
  color: ${(props) => props.theme.color.text.danger};
`;
